import type { DecisionQuestionInput } from '@takeit4me/shared'
import { ACKNOWLEDGEMENT, NO_REPLY_TAG } from './triage'

/**
 * Platform decision questions every company gets (design 9.1). They are
 * locked: a company can make them stricter but never switch them off
 * (design 13.3, "limits nobody can raise").
 */
const ALL: DecisionQuestionInput['applies_to'] = ['email', 'sms', 'chat', 'voice']
/** A message whose first line is only a request word: "person", "Agent.", "a human please", "PERSON!" (then perhaps a signature). */
export const BARE_PERSON = '^[^a-z0-9]*(?:(?:a|an|the)\\s+)?(?:(?:real|live)\\s+)?(?:person|human|agent|operator|representative|someone|somebody|staff(?:\\s+member)?|personne|persona|humain|humano)(?:\\s+(?:please|pls|now))?[^a-z0-9]*(?:\\n|$)'
const SCOPE = ' Judge only what the customer wrote, not quoted earlier messages or signatures.'

function q(x: Omit<DecisionQuestionInput, 'layer' | 'locked' | 'enabled' | 'unsure_low' | 'unsure_high' | 'rules' | 'applies_to'> & Partial<DecisionQuestionInput>): DecisionQuestionInput {
  return { layer: 'platform', locked: true, enabled: true, unsure_low: 0.45, unsure_high: 0.75, rules: [], applies_to: ALL, ...x }
}

export const PLATFORM_QUESTIONS: DecisionQuestionInput[] = [
  q({
    key: 'wants_person', type: 'noul', label: 'Wants a person',
    instructions: 'Does the customer ask to speak to, or hear from, a person rather than the AI?' + SCOPE,
    criteria: { true: 'Asks for a person, a human, staff, a manager, a callback from someone, or says they do not want to talk to a bot.', false: 'No such request.' },
    fires_on: ['true'], threshold: 0.5,
    outcome: { action: 'live_handover', reason: 'Asked for a person', reason_code: 'asked_for_person', priority: 1 },
    rules: [
      { pattern: '\\b(speak|talk|chat)\\s+(to|with)\\s+(a|an|some|the)?\\s*(human|person|real person|someone|somebody|agent|manager|staff)\\b', answer: 'true' },
      { pattern: '\\b(human|real person|operator|representative)\\s*(please|pls)?[.!]?\\s*$', answer: 'true' },
      // Every AI email says Reply "person" (format.ts): a reply that is just that word (or agent, human, operator),
      // on its own first line with any punctuation, always reaches a person without waiting on a model (A 5.2-10).
      { pattern: BARE_PERSON, answer: 'true' },
    ],
  }),
  q({
    key: 'legal_threat', type: 'noul', label: 'Legal threat',
    instructions: 'Does the customer threaten legal action, mention a lawyer or solicitor, a tribunal, an ombudsman or a regulator complaint?' + SCOPE,
    criteria: { true: 'A threat or mention of legal or regulatory action.', false: 'No such threat or mention.' },
    fires_on: ['true'], threshold: 0.5,
    outcome: { action: 'exception', reason: 'Legal threat', reason_code: 'risk_flag', priority: 1 },
  }),
  q({
    key: 'safety_incident', type: 'noul', label: 'Safety incident',
    instructions: 'Does the customer report an injury, accident, danger to someone, or a safety incident linked to the company?' + SCOPE,
    criteria: { true: 'An injury, accident, danger or safety incident is reported.', false: 'Nothing like that.' },
    fires_on: ['true'], threshold: 0.5,
    outcome: { action: 'exception', reason: 'Safety incident', reason_code: 'risk_flag', priority: 1 },
  }),
  q({
    key: 'vulnerability', type: 'noul', label: 'Signs of vulnerability',
    instructions: 'Does the message show signs that the customer may be vulnerable: bereavement, serious illness, financial hardship, distress, self-harm, abuse, or difficulty understanding?' + SCOPE,
    criteria: { true: 'One or more clear signs of vulnerability.', false: 'No clear signs.' },
    fires_on: ['true'], threshold: 0.55,
    outcome: { action: 'exception', reason: 'Signs of vulnerability', reason_code: 'risk_flag', priority: 1, tag: 'vulnerable' },
  }),
  q({
    key: 'complaint', type: 'noul', label: 'Complaint',
    instructions: 'Is the customer making a complaint about the company, its staff or its service?' + SCOPE,
    criteria: { true: 'A complaint about the company, its staff or its service.', false: 'A question, request or neutral feedback.' },
    fires_on: ['true'], threshold: 0.6,
    outcome: { action: 'hold', reason: 'Complaint: a person reads it before anything is sent', reason_code: 'risk_flag', priority: 2, tag: 'complaint' },
  }),
  q({
    key: 'frustration', type: 'score', label: 'Frustration',
    instructions: 'How frustrated is the customer?' + SCOPE,
    criteria: ['None: neutral or positive.', 'Mild: a minor inconvenience, politely expressed.', 'Material: clearly frustrated or annoyed.', 'Severe: angry, threatening or abusive.'],
    fires_on: ['2'], threshold: 0.6,
    outcome: { action: 'lower_autonomy', reason: 'Material frustration', priority: 2, tag: 'frustrated' },
  }),
  q({
    key: 'work_request', type: 'choice', label: 'Asks for work',
    instructions: 'Is the customer asking the company to carry out a piece of work for them, beyond answering a question or changing a single booking?' + SCOPE,
    criteria: {
      none: 'No: a question, a booking change, a complaint or anything else.',
      coding_task: 'Yes, a change to a website, app, form, report or integration.',
      business_task: 'Yes, business work such as invoices, statements, documents, bulk changes or data clean-up.',
    },
    fires_on: ['coding_task', 'business_task'], threshold: 0.6,
    outcome: { action: 'start_task', reason: 'Customer asked for work', priority: 3 },
    applies_to: ['email', 'sms', 'chat', 'voice'],
  }),
  q({
    key: 'auto_message', type: 'noul', label: 'Automatic message or spam',
    instructions: 'Is this an automatic message (out-of-office, bounce, notification, newsletter) or spam, rather than a person writing to the company?'
      // How to weigh the header and history signals, given as sender_signals.
      + ' When sender_signals are given, weigh them: failed authentication (SPF or DMARC) and a high provider spam score are strong signs of spam; passed authentication and a history of answered emails argue against spam, but neither decides on its own. A website form or relay may legitimately fail alignment or show a Reply-To mismatch.',
    // Clauses: a donation or sponsorship request is a person asking (a person decides), "unsubscribe" alone is
    // not spam, and a website form is spam only when what was typed into it is a pitch.
    criteria: {
      true: 'An automatic message, bulk mail, a sales pitch, a scam or nonsense. A website form counts only if the words typed into it are a sales pitch.',
      false: 'A person writing to the company, including website form submissions with a real request, requests for donations or sponsorship, and a person asking to unsubscribe or be removed.',
    },
    fires_on: ['true'], threshold: 0.7,
    outcome: { action: 'ignore', reason: 'Automatic message or spam', priority: 5 },
    applies_to: ['email', 'sms'],
  }),
  // Step 3, triage (design 5.2): a message that needs no reply. Not locked: a company may switch it off or narrow
  // its channels (an override of applies_to). The brain only acts on it when nothing else fired, the message has
  // no question in it, and our last message did not ask the customer something (core/triage.ts noReplyGuard).
  q({
    key: 'no_reply_needed', type: 'noul', label: 'No reply needed', locked: false,
    instructions: 'Is the customer\'s message only an acknowledgement, thanks or sign-off, with nothing to answer: no question, no request, no new information and no problem?' + SCOPE,
    criteria: {
      true: 'Only thanks, "ok", "got it", "great", "see you then" or similar, with nothing else.',
      false: 'Anything else: a question, a request, an answer to something we asked, new information, a complaint, or thanks together with something more.',
    },
    fires_on: ['true'], threshold: 0.85,
    outcome: { action: 'tag', reason: 'No reply needed', priority: 5, tag: NO_REPLY_TAG },
    applies_to: ['email', 'sms', 'chat'],
    rules: [{ pattern: ACKNOWLEDGEMENT, answer: 'true' }],
  }),
  // The three clarity checks on every task brief (design 10.3).
  q({
    key: 'ambiguity', type: 'noul', label: 'Ambiguity',
    instructions: 'Could this request reasonably mean more than one thing, so that two competent people could build different results from it?',
    criteria: { true: 'There is more than one reasonable reading.', false: 'Only one reasonable reading.' },
    fires_on: ['true'], threshold: 0.5,
    outcome: { action: 'tag', reason: 'Ambiguous brief', priority: 3, tag: 'ambiguous' },
    applies_to: ['task'],
  }),
  q({
    key: 'inconsistency', type: 'noul', label: 'Inconsistency',
    instructions: 'Does this request contradict itself, an earlier request, the customer records, policy, or the current system described in the state?',
    criteria: { true: 'There is a clash somewhere.', false: 'No clash.' },
    fires_on: ['true'], threshold: 0.5,
    outcome: { action: 'tag', reason: 'Inconsistent brief', priority: 3, tag: 'inconsistent' },
    applies_to: ['task'],
  }),
  q({
    key: 'incompleteness', type: 'noul', label: 'Incompleteness',
    instructions: 'Is information that the task needs (listed under required information in the state) missing from the request and the customer records?',
    criteria: { true: 'Required information is missing.', false: 'Everything required is present.' },
    fires_on: ['true'], threshold: 0.5,
    outcome: { action: 'tag', reason: 'Incomplete brief', priority: 3, tag: 'incomplete' },
    applies_to: ['task'],
  }),
]

/** Platform rules that nobody can raise (design 13.3). */
export const FIXED_RULES = [
  'Legal threats, safety incidents and clear vulnerability always go to a person.',
  'The AI never takes card details by voice.',
  'The AI never claims to be human.',
  'Agents never deploy without their template\'s gate and never see secrets.',
]
export const ALWAYS_PERSON_QUESTIONS = new Set(['legal_threat', 'safety_incident', 'vulnerability', 'wants_person'])
